import * as XLSX from "xlsx";

export type ParsedSheet = {
  headers: string[];
  rows: any[];          // array of objects (each object = row)
  groupName: string | null;
};

function isSupportedExt(name: string) {
  const ext = name.split(".").pop()?.toLowerCase();
  return ext === "xlsx" || ext === "xls" || ext === "csv" || ext === "ods" || ext === "vcf";
}

function inferGroupName(filename: string): string | null {
  const dot = filename.lastIndexOf(".");
  return dot > 0 ? filename.slice(0, dot) : filename || null;
}

function extractHeadersFromSheet(sheet: XLSX.WorkSheet): string[] {
  // Try header row first
  const headerRow = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1 })[0] || [];
  const headers = (headerRow as any[]).map((h) => String(h ?? "")).filter((h) => h.length > 0);
  return headers;
}

function extractRowsFromSheet(sheet: XLSX.WorkSheet): any[] {
  // Keep undefined cells as null (defval) so mapping is predictable
  return XLSX.utils.sheet_to_json(sheet, { defval: null });
}

class SHEETService {
  /**
   * Read a file and return parsed data from the FIRST worksheet.
   * NO internal state is persisted — caller owns the result.
   */
  readFile(selectedFile: File): Promise<ParsedSheet> {
    if (!isSupportedExt(selectedFile.name)) {
      return Promise.reject(new Error("Unsupported file type. Use xlsx, xls, csv, ods (vcf not recommended)."));
    }

    return new Promise<ParsedSheet>((resolve, reject) => {
      const reader = new FileReader();

      reader.onerror = () => reject(new Error("Failed to read the file."));
      reader.onabort = () => reject(new Error("File read was aborted."));

      reader.onload = () => {
        try {
          const fileData = new Uint8Array(reader.result as ArrayBuffer);
          const workbook = XLSX.read(fileData, { type: "array" });

          if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
            throw new Error("No sheets found in the workbook.");
          }

          const seen = new Set<string>();
          const unionHeaders: string[] = [];
          let allRows: any[] = [];

          for (const sheetName of workbook.SheetNames) {
            const sheet = workbook.Sheets[sheetName];
            if (!sheet) continue;

            let sheetHeaders = extractHeadersFromSheet(sheet);
            const sheetRows = extractRowsFromSheet(sheet);

            // Fallback: infer headers from first row's keys if header row is empty
            if (sheetHeaders.length === 0 && sheetRows.length > 0) {
              sheetHeaders = Object.keys(sheetRows[0] ?? {});
            }

            for (const h of sheetHeaders) {
              if (!seen.has(h)) { seen.add(h); unionHeaders.push(h); }
            }

            if (sheetRows.length > 0) {
              allRows = allRows.concat(sheetRows);
            }
          }

          if (unionHeaders.length === 0) {
            throw new Error("No data found in any sheet of the workbook.");
          }

          const groupName = inferGroupName(selectedFile.name);
          resolve({ headers: unionHeaders, rows: allRows, groupName });
        } catch (err) {
          reject(err instanceof Error ? err : new Error("Failed to parse the file."));
        }
      };

      // Always read as ArrayBuffer so we can pass to XLSX.read
      reader.readAsArrayBuffer(selectedFile);
    });
  }

  /**
   * Read multiple files and merge them into a single ParsedSheet.
   * - Headers: union of all files' headers, preserving order (first file first).
   * - Rows: concatenation of all files' rows.
   * - groupName: derived from the first file.
   */
  async readFiles(files: File[]): Promise<ParsedSheet> {
    if (files.length === 0) return { headers: [], rows: [], groupName: null };
    if (files.length === 1) return this.readFile(files[0]);

    const results = await Promise.all(files.map((f) => this.readFile(f)));

    // Union of all headers, preserving insertion order (first file sets priority)
    const seen = new Set<string>();
    const unionHeaders: string[] = [];
    for (const r of results) {
      for (const h of r.headers) {
        if (!seen.has(h)) { seen.add(h); unionHeaders.push(h); }
      }
    }

    const allRows = results.flatMap((r) => r.rows);

    const groupName = results[0]?.groupName ?? null;

    return { headers: unionHeaders, rows: allRows, groupName };
  }

  /**
   * No-op provided for API parity with the old service.
   * Keep it if callers expect to call `unselectFile()`.
   */
  unselectFile(): void {
    /* intentionally empty — stateless service */
  }
}

const sheetService = new SHEETService();
export default sheetService;
